import { AI_REPEATING_MESSAGE, eventBus } from '@3d-components/bus';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
    ConversationMessage,
} from '@store/actions/aiConversationAction';
import { get } from 'lodash';

export interface CourseData {
    id: number;
    grade: string;
    grade_value: number;
    collection_id: string;
    course_id?: string;
    course_name: string;
    // Exact name to hand to VTeacher; falls back to "Lớp <grade> - <course_name>".
    vteacher_course_name?: string | null;
    subject?: string;
}

export interface AIConversationState {
    currentSessionId: string | null;

    // Messages
    aiMessage: string;
    messages: ConversationMessage[];
    suggestedQuestions: string[] | null;
    courseList: CourseData[];
    currentCourse: CourseData | null;
    error: string | null;
    lastUpdated: string | null;
    vteacherUrl: string | null;

    // Loading states
    isLoading: boolean;
    isSending: boolean;
}

const initialState: AIConversationState = {
    currentSessionId: null,

    aiMessage: '',
    messages: [],
    suggestedQuestions: null,
    courseList: [],
    currentCourse: null,
    vteacherUrl: null,
    error: null,
    lastUpdated: null,

    // Loading states
    isLoading: false,
    isSending: false,
};



const aiConversationSlice = createSlice({
    name: 'aiConversation',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetState: () => initialState,
        clearMessages: (state) => {
            state.messages = [];
        },
        clearAIMessage: (state) => {
            state.aiMessage = '';
        },
        addMessage: (state, action: PayloadAction<ConversationMessage>) => {
            // Check if message already exists
            const existingIndex = state.messages.findIndex((msg) => msg.id === action.payload.id);
            if (existingIndex >= 0) {
                state.messages[existingIndex] = action.payload;
            } else {
                // Insert message in correct sequence order
                const insertIndex = state.messages.findIndex(
                    (msg) => msg.sequence_number > action.payload.sequence_number,
                );
                if (insertIndex >= 0) {
                    state.messages.splice(insertIndex, 0, action.payload);
                } else {
                    state.messages.push(action.payload);
                }
            }
        },
        createAIChatSession: (state, action) => {
            state.isLoading = true;
            state.error = null;
            state.aiMessage = ''
        },
        createAIChatSessionSuccess: (state, action) => {
            state.isLoading = false;
            state.currentSessionId = action.payload.session_id;
            state.messages = [action.payload.message];
            state.aiMessage = action.payload.message;
            eventBus.emit(AI_REPEATING_MESSAGE, get(action, 'payload.message'));
            state.suggestedQuestions = get(action, 'payload.messages[0].message_data.suggested_questions', []);
            state.lastUpdated = new Date().toISOString();
            state.error = null;
        },
        createAIChatSessionFailure: (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        },
        sendMessageWithAI: (state, action) => {
            state.isSending = true;
            state.error = null;
            state.aiMessage = ''
        },
        sendMessageWithAISuccess: (state, action) => {
            // Add both user message and AI response
            const { user_message, ai_response } = action.payload;

            state.suggestedQuestions = get(ai_response, 'message_data.suggested_questions', []);
            state.aiMessage = get(ai_response, 'content', '');
            eventBus.emit(AI_REPEATING_MESSAGE, get(ai_response, 'content', ''));
            // Add user message
            const userInsertIndex = state.messages.findIndex(
                (msg) => msg.sequence_number > user_message.sequence_number,
            );
            if (userInsertIndex >= 0) {
                state.messages.splice(userInsertIndex, 0, user_message);
            } else {
                state.messages.push(user_message);
            }

            // Add AI response
            const aiInsertIndex = state.messages.findIndex(
                (msg) => msg.sequence_number > ai_response.sequence_number,
            );
            if (aiInsertIndex >= 0) {
                state.messages.splice(aiInsertIndex, 0, ai_response);
            } else {
                state.messages.push(ai_response);
            }

            state.lastUpdated = new Date().toISOString();
            state.error = null;
            state.isSending = false;
        },
        sendMessageWithAIFailure: (state, action) => {
            state.isSending = false;
            state.error = action.payload;
        },
        fetchCourseList: (state) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchCourseListSuccess: (state, action) => {
            state.isLoading = false;
            state.courseList = action.payload;
            state.currentCourse = get(action, 'payload[0]', null);
        },
        fetchCourseListFailure: (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        },
        setCurrentCourse: (state, action) => {
            state.currentCourse = action.payload;
        },
        getVTeacherUrlRequest: (state, action: PayloadAction<{ id: number }>) => {
            state.isLoading = true;
            state.error = null;
        },
        getVTeacherUrlSuccess: (state, action) => {
            state.isLoading = false;
            state.vteacherUrl = action.payload.url;
        },
        getVTeacherUrlFailure: (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        },
    }
});

export const {
    clearError,
    resetState,
    clearMessages,
    addMessage,
    createAIChatSession,
    createAIChatSessionFailure,
    createAIChatSessionSuccess,
    sendMessageWithAI,
    sendMessageWithAIFailure,
    sendMessageWithAISuccess,
    fetchCourseList,
    fetchCourseListSuccess,
    fetchCourseListFailure,
    setCurrentCourse,
    clearAIMessage,
    getVTeacherUrlRequest,
    getVTeacherUrlSuccess,
    getVTeacherUrlFailure,
} = aiConversationSlice.actions;

export default aiConversationSlice.reducer;
